import { DragDropModule } from '@angular/cdk/drag-drop';
import { AfterViewInit, Component, Inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatPaginator, MatPaginatorModule } from '@angular/material/paginator';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Store } from '@ngrx/store';
import { BehaviorSubject, Subscription, map } from 'rxjs';
import { SharedModule } from '../../../../../../../app.module.shared';
import { ExperienceData, ExperienceSearchFilter } from '../../../../../../../models';
import { MunicipalityCapPipe } from '../../../../../../../pipes/municipality-code.pipe';
import { NationCodePipe } from '../../../../../../../pipes/nation-code.pipe';
import { experienceSearchAllRequest, experienceSearchDataReset, experienceSearchRequest } from '../../../../../../../store/experiences/actions-experiences';
import { getFoundExperiences } from '../../../../../../../store/experiences/selectors-experiences';
import { AppStore } from '../../../../../../../store/store';
import { InputCity } from '../../../../../../shared/input-city/input-city.component';
import { CountrySelectComponent } from '../../../../../../shared/input-country/input-country.component';
import { LoadingSpinnerComponent } from '../../../../../../shared/loading-spinner/loading-spinner.component';
import { RegionSelectComponent } from '../../../../../../shared/region-select/region-select.component';
import { COUNTRIES, Country } from '../../../../../../shared/utils';

@Component({
  selector: 'app-experiences-search-dialog',
  standalone: true,
  imports: [
    MatTableModule,
    MatPaginatorModule,
    MatTooltipModule,
    MatSortModule,
    MatDialogModule,
    DragDropModule,
    SharedModule,
    RegionSelectComponent,
    LoadingSpinnerComponent,
    CountrySelectComponent,
    InputCity,
    NationCodePipe,
    MunicipalityCapPipe
  ],
  templateUrl: './experiences-search-dialog.component.html',
  styleUrl: './experiences-search-dialog.component.css'
})
export class ExperiencesSearchDialogComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) sort!: MatSort;

  searchForm!: FormGroup;
  readonly displayedColumns: string[] = ['name', 'nation', 'region','municipality', 'legalName', 'actions'];
  private dataSourceSubject = new BehaviorSubject<MatTableDataSource<ExperienceData>>(new MatTableDataSource<ExperienceData>());
  dataSource$ = this.dataSourceSubject.asObservable();
  searchSubscription!: Subscription;

  constructor(
    private fb: FormBuilder,
    private store: Store<AppStore>,
    private dialogRef: MatDialogRef<ExperiencesSearchDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: any
  ) { 

  }

  ngOnInit(): void {
    this.initializeSearchForm();

    this.initializeNation();

    this.searchSubscription = this.store.select(getFoundExperiences)
      .pipe(map(foundExperiences => this.generateDataTableSource(foundExperiences)))
      .subscribe(dataSource => this.dataSourceSubject.next(dataSource));
  }

  ngAfterViewInit(): void {
    this.dataSource$.subscribe(dataSource => {
      if (dataSource) {
        dataSource.paginator = this.paginator;
        dataSource.sort = this.sort;
      }
    });
  }

  ngOnDestroy(): void {
    this.searchSubscription.unsubscribe();
  }

  onSearch() {
    const searchFilter = this.getSearchFilterFromForm();
    if (this.isEmptySearchFilter(searchFilter)) {
      this.store.dispatch(experienceSearchAllRequest());
    } else {
      this.store.dispatch(experienceSearchRequest({ searchFilter }));
    }
  }

  onClearFilter() {
    this.searchForm.reset();
    this.initializeNation();
    this.store.dispatch(experienceSearchDataReset());
  }

  pickExperience(experience: ExperienceData) {

    this.dialogRef.close(experience);

  }

  cancel() {
    this.dialogRef.close();
  }

  private initializeSearchForm() {
    this.searchForm = this.fb.group({
      name: [null],
      region: [null],
      nation: [null],
      municipality: [null],
      legalName: [null]
    });
  }

  private getSearchFilterFromForm(): ExperienceSearchFilter {
    const { region, nation, name, legalName, municipality } = this.searchForm.value;
    const filter: ExperienceSearchFilter = {};
    if (region) {
      filter.region = region;
    }
    if (nation) {
      filter.nation = nation.code;
    }
    if (municipality && municipality.length > 0) {
      filter.cap = [municipality[0]];
    }
    if (name) {
      filter.title = name;
    }
    if (legalName) {
      filter.legalName = legalName;
    }

    return filter;
  }

  private generateDataTableSource(foundExperiences: ExperienceData[]): MatTableDataSource<ExperienceData> {
    const matTableDataSource = new MatTableDataSource<ExperienceData>(foundExperiences);
    return matTableDataSource;
  }

  private isEmptySearchFilter(searchFilter: ExperienceSearchFilter) {
    return Object.values(searchFilter).every(value => !value);
  }

  private initializeNation() {
    const italy = COUNTRIES.find(c => c.code === 'IT');
    this.searchForm.patchValue({
      nation: italy || null
    });

    this.searchForm.get('nation')?.valueChanges.subscribe((value: Country) => {
      if (value && value.code != "IT") {
        this.searchForm.get('municipality')?.setValue(null, { emitEvent: false });
        this.searchForm.get('municipality')?.disable({ emitEvent: false });
      } else {
        this.searchForm.get('municipality')?.enable({ emitEvent: false });
      }
    });
  }

  isForeignNation(): boolean {
    if (this.searchForm.get('nation')?.value.code != "IT") {
      return true;
    } else {
      return false
    }
  }
}